import React, { useState } from 'react';

const ENTRY_TYPES = [
  { id: 'weight',   label: 'Weight',      bg: '#E8F5EE', color: 'var(--forest-mid)' },
  { id: 'health',   label: 'Health',      bg: '#F4E8F1', color: 'var(--burg-light)' },
  { id: 'feed',     label: 'Feed',        bg: '#FEF3E2', color: 'var(--warning)' },
  { id: 'expense',  label: 'Expense',     bg: '#F4E8F1', color: 'var(--burg)' },
  { id: 'observe',  label: 'Observation', bg: '#E8F5EE', color: 'var(--forest-light)' },
  { id: 'photo',    label: 'Photo',       bg: '#E8F0EC', color: 'var(--forest-mid)' },
];

const MEDS = [
  { label: 'Select medication…', withdraw: 0 },
  { label: 'CD&T Vaccination', withdraw: 0 },
  { label: 'Ivermectin (pour-on)', withdraw: 35 },
  { label: 'SafeGuard (dewormer)', withdraw: 1 },
  { label: 'Penicillin', withdraw: 8 },
  { label: 'LA-200 (oxytetracycline)', withdraw: 28 },
  { label: 'Other (custom)', withdraw: null },
];

const MOODS = ['😄','😐','😟','🤒'];
const MOOD_LABELS = ['Great','Normal','Concerning','Sick'];

export default function LogEntry({ onNavigate }) {
  const [selected, setSelected] = useState(null);
  const [med, setMed] = useState(0);
  const [mood, setMood] = useState(null);
  const [saved, setSaved] = useState(false);
  const [customWithdraw, setCustomWithdraw] = useState('');

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => { setSaved(false); setSelected(null); setMood(null); setMed(0); }, 1800);
  };

  const withdraw = MEDS[med];
  const showWithdraw = selected === 'health' && withdraw && withdraw.withdraw > 0;
  const showCustom = selected === 'health' && withdraw && withdraw.withdraw === null;

  return (
    <div style={{overflowY:'auto', height:'100%', paddingBottom:24}}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerName}>Log for Clover</div>
        <div style={s.headerSub}>Saturday, March 21 · Emma Dawson</div>
      </div>

      <SectionLabel>What are you logging?</SectionLabel>

      {/* Entry type grid */}
      <div style={s.typeGrid}>
        {ENTRY_TYPES.map(t => (
          <button key={t.id} style={s.typeBtn(selected === t.id)} onClick={() => setSelected(t.id === selected ? null : t.id)}>
            <div style={{...s.typeIcon, background: t.bg}}>
              <EntryIcon id={t.id} color={t.color} />
            </div>
            <span style={s.typeLbl}>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Forms */}
      {selected && (
        <div style={s.form} className="fade-in">

          {/* ---- WEIGHT ---- */}
          {selected === 'weight' && (
            <>
              <FormTitle>Record Weight</FormTitle>
              <Field label="Animal">
                <select style={sf.select}><option>Clover (LaMancha doe)</option></select>
              </Field>
              <Field label="Weight">
                <div style={{display:'flex',gap:8}}>
                  <input type="number" defaultValue="68" style={{...sf.input, flex:2}} />
                  <select style={{...sf.select, flex:1}}><option>lbs</option><option>kg</option></select>
                </div>
              </Field>
              <Field label="Notes (optional)">
                <textarea style={sf.textarea} placeholder="Body condition, activity level…" />
              </Field>
              <SaveBtn onSave={handleSave} saved={saved} />
            </>
          )}

          {/* ---- HEALTH ---- */}
          {selected === 'health' && (
            <>
              <FormTitle>Record Health Event</FormTitle>
              <Field label="Event Type">
                <select style={sf.select} onChange={e => setMed(parseInt(e.target.value))}>
                  {MEDS.map((m,i) => <option key={i} value={i}>{m.label}</option>)}
                </select>
              </Field>
              {showCustom && (
                <Field label="Withdrawal period (days)">
                  <input type="number" style={sf.input} placeholder="Enter days" value={customWithdraw} onChange={e => setCustomWithdraw(e.target.value)} />
                </Field>
              )}
              <Field label="Notes">
                <textarea style={sf.textarea} placeholder="Describe the health event, dose, route…" />
              </Field>
              {showWithdraw && (
                <div style={s.withdrawAlert}>
                  <div style={s.withdrawTitle}>⚠ Withdrawal Period Active</div>
                  <div style={s.withdrawBody}>
                    <strong>{withdraw.withdraw}-day</strong> withdrawal period begins today. Do not sell or show until <strong>April {21 + withdraw.withdraw}</strong>.<br/>
                    <span style={{color:'var(--burg)', fontWeight:500}}>Lewis County Fair (July 18)</span> is outside this window — you're clear.
                  </div>
                  <div style={s.withdrawDisclaimer}>Always consult your veterinarian for treatment decisions.</div>
                </div>
              )}
              <SaveBtn onSave={handleSave} saved={saved} />
            </>
          )}

          {/* ---- FEED ---- */}
          {selected === 'feed' && (
            <>
              <FormTitle>Record Feeding</FormTitle>
              <Field label="Feed Type">
                <select style={sf.select}>
                  <option>Grain mix</option>
                  <option>Hay — orchard grass</option>
                  <option>Hay — alfalfa</option>
                  <option>Mineral supplement</option>
                  <option>Other</option>
                </select>
              </Field>
              <Field label="Amount">
                <div style={{display:'flex',gap:8}}>
                  <input type="number" defaultValue="2.5" style={{...sf.input, flex:2}} />
                  <select style={{...sf.select, flex:1}}><option>lbs</option><option>oz</option><option>flakes</option></select>
                </div>
              </Field>
              <Field label="Cost (if purchased today)">
                <input type="text" placeholder="$0.00" style={sf.input} />
              </Field>
              <SaveBtn onSave={handleSave} saved={saved} />
            </>
          )}

          {/* ---- EXPENSE ---- */}
          {selected === 'expense' && (
            <>
              <FormTitle>Record Expense</FormTitle>
              <Field label="Category">
                <select style={sf.select}>
                  <option>Feed</option>
                  <option>Veterinary</option>
                  <option>Equipment</option>
                  <option>Entry fees</option>
                  <option>Supplies</option>
                  <option>Transportation</option>
                </select>
              </Field>
              <Field label="Amount">
                <input type="text" placeholder="$0.00" style={sf.input} />
              </Field>
              <Field label="Description">
                <input type="text" placeholder="What did you purchase?" style={sf.input} />
              </Field>
              <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:14}}>
                <input type="checkbox" id="receipt" style={{width:16, height:16, accentColor:'var(--forest-mid)'}} />
                <label htmlFor="receipt" style={{fontSize:13, color:'var(--text-body)'}}>Attach receipt photo</label>
              </div>
              <SaveBtn onSave={handleSave} saved={saved} />
            </>
          )}

          {/* ---- OBSERVATION ---- */}
          {selected === 'observe' && (
            <>
              <FormTitle>Daily Observation</FormTitle>
              <Field label="How does Clover seem today?">
                <div style={{display:'flex', gap:8, marginTop:4}}>
                  {MOODS.map((emoji, i) => (
                    <button key={i} style={s.moodBtn(mood === i, i === 3)}
                      onClick={() => setMood(i)}>
                      <span style={{fontSize:22}}>{emoji}</span>
                      <span style={{fontSize:10, color: mood===i ? '#fff' : 'var(--text-muted)', display:'block', marginTop:2}}>{MOOD_LABELS[i]}</span>
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Notes">
                <textarea style={sf.textarea} placeholder="What did you notice today? Eating well? Any changes in behavior?" />
              </Field>
              <SaveBtn onSave={handleSave} saved={saved} />
            </>
          )}

          {/* ---- PHOTO ---- */}
          {selected === 'photo' && (
            <>
              <FormTitle>Add Photo</FormTitle>
              <div style={s.photoZone}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" style={{margin:'0 auto 10px', display:'block'}}>
                  <rect x="2" y="7" width="20" height="14" rx="2" stroke="var(--text-muted)" strokeWidth="2"/>
                  <circle cx="12" cy="13" r="3" stroke="var(--text-muted)" strokeWidth="2"/>
                  <path d="M8 7l2-3h4l2 3" stroke="var(--text-muted)" strokeWidth="2"/>
                </svg>
                <div style={{fontSize:13, color:'var(--text-muted)'}}>Tap to take photo or choose from library</div>
                <div style={{fontSize:11, color:'var(--text-muted)', marginTop:4}}>Date stamp applied automatically</div>
              </div>
              <Field label="Caption (optional)">
                <input type="text" placeholder="Describe what's in the photo…" style={sf.input} />
              </Field>
              <SaveBtn onSave={handleSave} saved={saved} />
            </>
          )}
        </div>
      )}

      {/* Success toast */}
      {saved && (
        <div style={s.toast} className="fade-in">
          <span style={{fontSize:16}}>✓</span> Entry saved to Clover's record book
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }) {
  return <div style={{fontSize:10, fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--text-muted)', padding:'14px 16px 8px'}}>{children}</div>;
}
function FormTitle({ children }) {
  return <div style={{fontSize:14, fontWeight:600, color:'var(--text-head)', marginBottom:14}}>{children}</div>;
}
function Field({ label, children }) {
  return (
    <div style={{marginBottom:12}}>
      <label style={{fontSize:10, fontWeight:600, color:'var(--text-muted)', letterSpacing:'0.07em', textTransform:'uppercase', display:'block', marginBottom:4}}>{label}</label>
      {children}
    </div>
  );
}
function SaveBtn({ onSave, saved }) {
  return (
    <button style={s.saveBtn(saved)} onClick={onSave} disabled={saved}>
      {saved ? '✓ Saved' : 'Save Entry'}
    </button>
  );
}

const s = {
  header: { background:'var(--forest-primary)', padding:'14px 16px' },
  headerName: { fontFamily:'var(--ff-display)', fontSize:20, color:'#fff' },
  headerSub:  { fontSize:12, color:'var(--forest-pale)', marginTop:2 },
  typeGrid: {
    display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, padding:'0 16px',
  },
  typeBtn: (active) => ({
    background: active ? '#F0F8F4' : 'var(--surface)',
    border: active ? '1.5px solid var(--forest-mid)' : '1px solid var(--border)',
    borderRadius: 12,
    padding:'13px 6px',
    display:'flex', flexDirection:'column', alignItems:'center', gap:6,
    cursor:'pointer', transition:'all 0.15s',
  }),
  typeIcon: {
    width:38, height:38, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center',
  },
  typeLbl: { fontSize:11, fontWeight:500, color:'var(--text-head)' },
  form: {
    margin:'12px 16px 0',
    background:'var(--surface)',
    border:'1px solid var(--border)',
    borderRadius:12,
    padding:16,
  },
  withdrawAlert: {
    background:'var(--burg-pale)',
    border:'1px solid #D4A0B8',
    borderRadius:8,
    padding:'10px 12px',
    marginBottom:14,
  },
  withdrawTitle: { fontSize:12, fontWeight:700, color:'var(--burg)', marginBottom:4 },
  withdrawBody:  { fontSize:12, color:'var(--burg-light)', lineHeight:1.5 },
  withdrawDisclaimer: { fontSize:10, color:'var(--burg-light)', marginTop:6, fontStyle:'italic' },
  moodBtn: (active, danger) => ({
    flex:1,
    background: active ? (danger ? 'var(--danger)' : 'var(--forest-mid)') : 'var(--surface)',
    border: `1.5px solid ${active ? (danger ? 'var(--danger)' : 'var(--forest-mid)') : 'var(--border)'}`,
    borderRadius:10,
    padding:'8px 4px',
    cursor:'pointer',
    textAlign:'center',
    transition:'all 0.15s',
  }),
  photoZone: {
    background:'#F8FAF9',
    border:'1.5px dashed var(--border)',
    borderRadius:10,
    padding:'28px 16px',
    textAlign:'center',
    cursor:'pointer',
    marginBottom:14,
  },
  saveBtn: (saved) => ({
    width:'100%',
    background: saved ? 'var(--forest-light)' : 'var(--forest-mid)',
    color:'#fff', border:'none',
    borderRadius:10, padding:13,
    fontSize:14, fontWeight:600,
    transition:'background 0.2s',
  }),
  toast: {
    position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)',
    background:'var(--forest-sidebar)', color:'#fff',
    padding:'10px 20px', borderRadius:24,
    fontSize:13, fontWeight:500,
    display:'flex', alignItems:'center', gap:8,
    boxShadow:'0 4px 20px rgba(0,0,0,0.2)',
    whiteSpace:'nowrap',
  },
};

const sf = {
  input: {
    width:'100%', background:'#F8FAF9', border:'1px solid var(--border)',
    borderRadius:8, padding:'8px 10px', fontSize:13, color:'var(--text-head)', outline:'none',
  },
  select: {
    width:'100%', background:'#F8FAF9', border:'1px solid var(--border)',
    borderRadius:8, padding:'8px 10px', fontSize:13, color:'var(--text-head)', outline:'none',
  },
  textarea: {
    width:'100%', background:'#F8FAF9', border:'1px solid var(--border)',
    borderRadius:8, padding:'8px 10px', fontSize:13, color:'var(--text-head)', outline:'none',
    height:72, resize:'none',
  },
};

function EntryIcon({ id, color }) {
  switch(id) {
    case 'weight': return <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M6 8h12l-1.5 9H7.5L6 8z" stroke={color} strokeWidth="2"/><path d="M10 8V6a2 2 0 114 0v2" stroke={color} strokeWidth="2"/></svg>;
    case 'health': return <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 21C12 21 4 13.5 4 8.5a5 5 0 0110 0 5 5 0 0110 0C24 13.5 12 21 12 21z" stroke={color} strokeWidth="2"/></svg>;
    case 'feed':   return <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M3 11l9-9 9 9" stroke={color} strokeWidth="2" strokeLinecap="round"/><path d="M5 11v7a2 2 0 002 2h10a2 2 0 002-2v-7" stroke={color} strokeWidth="2"/></svg>;
    case 'expense':return <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="6" width="20" height="14" rx="2" stroke={color} strokeWidth="2"/><path d="M2 10h20" stroke={color} strokeWidth="2"/></svg>;
    case 'observe':return <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M4 6h16M4 10h16M4 14h10" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case 'photo':  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="7" width="20" height="14" rx="2" stroke={color} strokeWidth="2"/><circle cx="12" cy="13" r="3" stroke={color} strokeWidth="2"/><path d="M8 7l2-3h4l2 3" stroke={color} strokeWidth="2"/></svg>;
    default: return null;
  }
}
